import {
  SiHtml5,
  SiCss3,
  SiPython,
  SiFlask,
  SiBootstrap,
  SiReact,
  SiTailwindcss,
  SiTypescript,
  SiWordpress,
  SiJavascript,
  SiFastapi,
  SiExpress,
  SiPostgresql,
  SiElasticsearch,
  SiElectron,
  SiApachetomcat,
  SiApachemaven,
  SiRedis,
  SiFlutter,
  SiDart,
  SiFirebase,
  SiGoogledrive,
  SiNextdotjs,
  SiFramer,
  SiVite,
  SiNodedotjs,
} from "react-icons/si";
import { FaJava } from "react-icons/fa";
import { IconType } from "react-icons";

// Default background configuration
export const defaultBackground = {
  base: [0.05, 0.01, 0.02] as [number, number, number],
  accent: [0.1, 0.0, 0.2] as [number, number, number],
  intensity: 0.7,
  mixStrength: 0.4,
};

export type ProjectProps = {
  id: number;
  name: string;
  description: string;
  pageDescription?: string; // Detailed description for project page
  technologies: IconType[];
  techNames: string[];
  techLinks: string[];
  github: string;
  demo: string;
  image: string;
  imageCaptions?: string[];
  release?: string;
  archive?: {
    title: string;
    description: string;
    demo: string;
    walkthrough: string;
    poster: string;
  };
  showcase?: {
    src: string;
    poster: string;
    title: string;
    description: string;
    captions: string;
  };
  videoDemoLabel?: string;
  videoExplanationLabel?: string;
  images?: string[]; // Additional images for the slider
  available: boolean;
  backgroundColors: {
    base: [number, number, number];
    accent: [number, number, number];
    intensity: number;
    mixStrength: number;
  };
  videoDemo?: string; // YouTube embed URL
  videoExplanation?: string; // Local video path
  featured?: boolean; // Mark as featured project
};

export const projects: ProjectProps[] = [
  {
    id: 0,
    name: "Sentinel Prime",
    description:
      "Enterprise-grade cybersecurity monitoring ecosystem with real-time threat detection, behavioral analytics, and centralized administration.",
    pageDescription: `Sentinel is a complete, working cybersecurity monitoring ecosystem that demonstrates enterprise-grade security capabilities in an integrated platform. This project successfully combines three specialized components to provide comprehensive organizational security visibility and threat detection.

The Three-Component Architecture:

• SentinelApp - Desktop agent with 7 specialized monitors (Login, Process, Network, Filesystem, System, Browser, USB)
• SentinelUBA - Python-based analytics engine with automated log analysis and risk scoring
• SentinelPrime - React-based admin dashboard for security operations and user management

Complete Security Pipeline: MONITOR → ANALYZE → DETECT → ALERT

Key Features:

• 7 Specialized Monitors - Comprehensive endpoint visibility through Login, Process, Network, Filesystem, System, Browser, and USB activity monitoring

• Real-time Threat Detection - Integrated MalwareBazaar for automated malware identification and classification

• Behavioral Analytics Engine - Automated risk scoring with 5-tier classification system to assess and prioritize security events

• Production-Ready Architecture - Scalable system designed for 10-500 endpoints with enterprise capabilities

• Complete Data Flow - Seamless integration from data collection through analysis to alerting for unified security monitoring

• Modern Tech Stack - Built with Electron (desktop agent), React (dashboard), Python with FastAPI (backend), OpenSearch (data indexing), and Supabase (database management)

This is a fully functional demonstration project that proves how modern security monitoring systems operate. All core features are implemented and functional, from endpoint data collection to real-time dashboard updates and threat intelligence integration.`,
    technologies: [
      SiReact,
      SiPython,
      SiFastapi,
      SiExpress,
      SiTypescript,
      SiElectron,
      SiPostgresql,
      SiElasticsearch,
    ],
    techNames: [
      "React",
      "Python",
      "FastAPI",
      "Express.js",
      "TypeScript",
      "Electron",
      "PostgreSQL",
      "OpenSearch",
    ],
    techLinks: [
      "https://react.dev/",
      "https://www.python.org/",
      "https://fastapi.tiangolo.com/",
      "https://expressjs.com/",
      "https://www.typescriptlang.org/",
      "https://www.electronjs.org/",
      "https://www.postgresql.org/",
      "https://opensearch.org/",
    ],
    github: "",
    demo: "",
    image: "/images/sentinel/sentinel mockup.png",
    images: [
      "/images/sentinel/sentinel mockup.png",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201241.580.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201313.484.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201323.253.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201332.926.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201339.286.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201350.325.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201401.494.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201403.046.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201405.910.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201408.454.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201411.894.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201414.502.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201416.510.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201422.773.jpg",
      "/images/sentinel/2025-10-23 17-49-08.mkv_20251102_201440.462.jpg",
    ],
    available: true,
    backgroundColors: {
      base: [0.02, 0.03, 0.08] as [number, number, number], // Dark blue base
      accent: [0.08, 0.12, 0.25] as [number, number, number], // Deep blue accent
      intensity: 0.7,
      mixStrength: 0.4,
    },
    videoDemo: "https://www.youtube.com/embed/Ta37wvoaKLo",
    videoExplanation: "/images/sentinel/The_Sentinel_Ecosystem.mp4",
    featured: true,
  },
  {
    id: 1,
    name: "Aiden 2.0",
    release: "New release",
    archive: {
      title: "Aiden 1.0 — where it started",
      description: "The original voice assistant, before the new workspace and desktop overlay. Watch the first demonstration and project walkthrough.",
      demo: "https://www.youtube.com/embed/n6l9Kr35POQ",
      walkthrough: "/images/aiden/Aiden__Windows_AI_Assistant.mp4",
      poster: "/images/aiden/aiden cover.png",
    },
    description:
      "A Windows AI assistant with voice and text conversations, a live desktop overlay, media controls, scheduled actions, and smart-home automation.",
    pageDescription: `Aiden brings everyday PC tasks into one conversation. Open an app, find music, schedule an action, or control a connected device through a redesigned dashboard and an optional Electron desktop overlay. A Python service coordinates speech, AI planning, Windows commands, and live updates across both interfaces.

A redesigned workspace:
• Shared Chat and Voice studio with conversation history and context restoration
• Silent text chat, immediate sent-message feedback, and duplicate-reply reconciliation
• Responsive desktop navigation and a mobile bottom bar
• Warm studio, Aiden blue, and Midnight blue themes remembered by the browser
• Searchable settings for appearance, AI models, prompts, speech, and integrations
• Live PC metrics, service status, and ESP32 device controls in a dedicated panel

Voice that stays in view:
• Activate with a local wake word, a global hotkey, or the dashboard
• Choose the PC microphone or a supported browser's microphone
• Click-through Electron overlay with an edge glow and a compact voice capsule
• Listening, thinking, and speaking states with live transcript previews
• Word-following reply captions driven by Edge TTS timing data
• Voice replies play on the device being used, with queued playback, mute, and retry controls

PC control and scheduled actions:
• Launch installed Windows applications and Steam games
• Close applications and process groups, with command execution kept in sequence
• Lock, sleep, restart, shut down, and adjust supported audio and display settings
• Run multi-step requests and retain context for follow-up questions
• Create timers and delayed commands, then inspect or cancel them in the dashboard

Media and smart home:
• Route playback commands to Spotify, YouTube, YouTube Music, and Jellyfin
• Pause supported active media while listening and restore it after the interaction
• Recover Jellyfin sessions independently so one unavailable session does not block the rest
• Control configured ESP32 devices, including fan power, speed, and timer options
• Show connection state and action feedback for connected devices

Speech and AI architecture:
• Groq Whisper transcribes PC voice commands; Vosk provides local live-caption previews
• Vosk, Porcupine, and openWakeWord are supported local wake-word options
• Groq, Gemini, and Ollama provide configurable AI responses
• Edge TTS supplies speech and word timings, with Google Cloud TTS and offline voice options
• Microphone readiness, voice-activity detection, audio caching, and cancellation paths support the speech lifecycle
• Playback can begin while audio and timing data are persisted; stage-level tracing helps identify latency

Built across three interfaces:
• Backend: Python, FastAPI, Uvicorn, asyncio, REST, and WebSockets
• Dashboard: React 18, JavaScript, Tailwind CSS, Radix/shadcn UI, and Framer Motion
• Desktop overlay: Electron, React 19, TypeScript, Vite, and Framer Motion
• Persistence: Neon PostgreSQL, SQLAlchemy, and Redis for history, preferences, timers, and context
• Windows integration: system tray, global hotkeys, process management, and audio-session control
• Wake detection runs locally; cloud transcription and cloud AI or speech providers require a connection. Ollama supports local model responses.`,
    technologies: [
      SiPython,
      SiFastapi,
      SiElectron,
      SiReact,
      SiTypescript,
      SiJavascript,
      SiTailwindcss,
      SiRedis,
      SiPostgresql,
      SiVite,
    ],
    techNames: [
      "Python",
      "FastAPI",
      "Electron",
      "React",
      "TypeScript",
      "JavaScript",
      "Tailwind CSS",
      "Redis",
      "PostgreSQL",
      "Vite",
    ],
    techLinks: [
      "https://www.python.org/",
      "https://fastapi.tiangolo.com/",
      "https://www.electronjs.org/",
      "https://react.dev/",
      "https://www.typescriptlang.org/",
      "https://developer.mozilla.org/en-US/docs/Web/JavaScript",
      "https://tailwindcss.com/",
      "https://redis.io/",
      "https://www.postgresql.org/",
      "https://vite.dev/",
    ],
    github: "",
    demo: "",
    image: "/images/aiden/v2/aiden-2-cover-shots.webp",
    images: [
      "/images/aiden/v2/dashboard.webp",
      "/images/aiden/v2/conversation.webp",
      "/images/aiden/v2/voice-studio.webp",
      "/images/aiden/v2/appearance.webp",
      "/images/aiden/v2/pc-and-devices.webp",
      "/images/aiden/v2/overlay-listening.webp",
      "/images/aiden/v2/overlay-thinking.webp",
      "/images/aiden/v2/overlay-replying.webp",
      "/images/aiden/v2/mobile-voice.webp",
      "/images/aiden/v2/mobile-devices.webp",
    ],
    imageCaptions: [
      "The new conversation workspace in Warm studio",
      "A real conversation with Aiden",
      "Voice studio with a choice of microphone source",
      "Three appearance themes and searchable settings",
      "Live PC readings and connected ESP32 controls",
      "Desktop overlay: listening with live transcript previews",
      "Desktop overlay: thinking through a request",
      "Desktop overlay: replying with word-following captions",
      "Voice studio on mobile",
      "PC and smart-home controls on mobile",
    ],
    showcase: {
      src: "/images/aiden/v2/aiden-2-showcase.mp4",
      poster: "/images/aiden/v2/showcase-poster.webp",
      title: "Meet Aiden 2.0.",
      description: "Watch Aiden control Spotify and Jellyfin, combine music and smart-home actions, schedule app launches, app closing and PC locking, and report UPS status—with narration, music, and Aiden’s own voice.",
      captions: "/images/aiden/v2/showcase-captions.vtt",
    },
    available: true,
    backgroundColors: {
      base: [0.05, 0.01, 0.08] as [number, number, number], // Dark purple base
      accent: [0.15, 0.08, 0.25] as [number, number, number], // Deep purple accent
      intensity: 0.7,
      mixStrength: 0.4,
    },
    featured: true,
  },
  {
    id: 2,
    name: "DocuMate",
    description:
      "Your ultimate solution for managing and organizing important documents with smart OCR and beautiful dark UI.",
    pageDescription: `DocuMate is your ultimate solution for managing and organizing important documents. Whether it's your insurance card, driver's license, or regional office paperwork, DocuMate provides a secure and convenient way to keep all your crucial documents in one place.

Key Features:

• Adaptive Theme System - Seamlessly switch between Dark, Light, and AMOLED Black themes
• End-to-End Encryption - Military-grade AES-256 encryption for all documents
• Encrypted Google Drive Backup - Your documents are encrypted locally before uploading to your own Google Drive
• Biometric Authentication - Fingerprint and face unlock support
• Advanced OCR Scanning - Powered by Google ML Kit for accurate text extraction
• Auto-Categorization - Smart document type detection
• Smart Expiry Reminders - Customizable notifications before documents expire
• Full-Text Search - Find any document or text within documents instantly
• Offline-First Design - Full functionality without internet connection

Security & Privacy First:
• Complete Transparency - 100% open-source code
• Your Data, Your Control - Documents stored on YOUR Google Drive
• Client-Side Encryption - All encryption happens on your device
• Zero-Knowledge Architecture - We cannot access your documents
• No Analytics/Tracking - We don't collect usage data

Technology Stack:
• Flutter 3.0+ & Dart 3.0+
• Hive (Local NoSQL database)
• flutter_secure_storage & encrypt (Security)
• Google Drive API & Firebase Auth (Cloud)
• Google ML Kit & OpenCV (Document Processing)
• Local Auth (Biometrics)`,
    technologies: [
      SiFlutter,
      SiDart,
      SiFirebase,
      SiGoogledrive,
    ],
    techNames: [
      "Flutter",
      "Dart",
      "Firebase",
      "Google Drive",
    ],
    techLinks: [
      "https://flutter.dev/",
      "https://dart.dev/",
      "https://firebase.google.com/",
      "https://developers.google.com/drive",
    ],
    github: "https://github.com/SahiDemon/DocuMate",
    demo: "https://sahidemon.github.io/DocuMate/",
    image: "/images/documate/documate_mockup.png",
    images: [
      "/images/documate/documate_mockup.png",
      "/images/documate/129_1x_shots_so.png",
      "/images/documate/362_1x_shots_so.png",
      "/images/documate/43_1x_shots_so.png",
      "/images/documate/670_1x_shots_so.png",
      "/images/documate/928_1x_shots_so.png",
    ],
    available: true,
    backgroundColors: {
      base: [0.0, 0.0, 0.0] as [number, number, number], // Black
      accent: [0.1, 0.1, 0.44] as [number, number, number], // Midnight Blue
      intensity: 0.8,
      mixStrength: 0.5,
    },
    videoDemo: "https://player.vimeo.com/video/1141496061",
    videoExplanation: "",
    featured: true,
  },
  {
    id: 3,
    name: "AssembleX",
    description:
      "AssembleX is a Sri Lankan web and app development agency specializing in creating stunning websites and innovative mobile applications.",
    pageDescription: `AssembleX is a Sri Lankan web and app development agency that stands at the forefront of digital innovation. We specialize in creating stunning websites and innovative mobile applications that captivate users and drive business growth.

Our comprehensive suite of services includes:
• Custom Web Development: Crafting responsive, modern websites tailored to your brand
• Mobile App Development: Building native and cross-platform applications
• UI/UX Design: Creating intuitive and engaging user experiences
• Digital Marketing: Implementing effective strategies for online growth
• E-commerce Solutions: Developing secure and scalable online stores
• Content Management: Providing easy-to-use systems for content updates

At AssembleX, we combine technical expertise with creative design to deliver solutions that not only meet but exceed client expectations.`,
    technologies: [SiWordpress, SiJavascript, SiHtml5, SiCss3],
    techNames: ["WordPress", "JavaScript", "HTML5", "CSS3"],
    techLinks: [
      "https://wordpress.org/",
      "https://developer.mozilla.org/en-US/docs/Web/JavaScript",
      "https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/HTML5",
      "https://developer.mozilla.org/en-US/docs/Web/CSS",
    ],
    github: "",
    demo: "http://assemblex.live/",
    image: "/projects/assemblex.png",
    images: [
      "/projects/assemblex.png",
      "/images/assemblex/Screenshot 2024-06-30 011228.png",
      "/images/assemblex/Screenshot 2025-10-19 233111.png",
      "/images/assemblex/Screenshot 2025-10-19 233138.png",
      "/images/assemblex/Screenshot 2025-10-19 233202.png",
      "/images/assemblex/Screenshot 2025-10-19 233235.png",
      "/images/assemblex/Screenshot 2025-10-19 233259.png",
    ],
    available: true,
    backgroundColors: {
      base: [0.01, 0.0, 0.0] as [number, number, number], // Black for AssembleX
      accent: [0.92, 0.9, 0.87] as [number, number, number], // Cream-White for AssembleX
      intensity: 0.4,
      mixStrength: 0.2,
    },
  },
  {
    id: 4,
    name: "AssembleZ",
    description:
      "AssembleZ is a platform designed to simplify the process of finding and comparing PC components in Sri Lanka.",
    pageDescription: `AssembleZ revolutionizes the way tech enthusiasts in Sri Lanka build their dream computers. Our platform offers a comprehensive solution for comparing and assembling PC components with real-time price tracking across major retailers.

Key Features:
• Interactive PC Builder: Drag-and-drop interface for easy component selection
• Price Comparison: Real-time price tracking across multiple retailers
• Compatibility Checker: Automatic verification of component compatibility
• Performance Metrics: Detailed benchmarks and performance estimates
• Price History: Historical price trends for informed purchasing
• Save Builds: Store and share your custom PC configurations

The platform is designed to make the PC building process accessible to everyone, from beginners to experienced builders, while ensuring they get the best value for their investment.`,
    technologies: [SiPython, SiFlask, SiBootstrap, SiJavascript],
    techNames: ["Python", "Flask", "Bootstrap", "HTML5", "CSS3", "JavaScript"],
    techLinks: [
      "https://www.python.org/",
      "https://flask.palletsprojects.com/",
      "https://getbootstrap.com/",
      "https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/HTML5",
      "https://developer.mozilla.org/en-US/docs/Web/CSS",
      "https://developer.mozilla.org/en-US/docs/Web/JavaScript",
    ],
    github: "",
    demo: "https://www.assemblez.tech/",
    image: "/projects/assemblez.png",
    images: [
      "/projects/assemblez.png",
      "/images/assemblez/1.png",
      "/images/assemblez/2.png",
      "/images/assemblez/3.png",
      "/images/assemblez/4.png",
    ],
    available: true,
    backgroundColors: {
      base: [0.05, 0.01, 0.02] as [number, number, number],
      accent: [0.4, 0.0, 0.0] as [number, number, number],
      intensity: 0.8,
      mixStrength: 0.4,
    },
  },
  {
    id: 5,
    name: "GreenMart",
    description:
      "GreenMart is an e-commerce application facilitating the online purchase of fresh vegetables, fruits, and groceries with secure transactions.",
    pageDescription: `GreenMart is a comprehensive e-commerce platform that bridges the gap between local farmers and consumers, providing fresh produce directly to your doorstep. Built with Java and modern web technologies, it offers a seamless shopping experience for both customers and vendors.

Platform Features:
• User-Friendly Interface: Intuitive navigation and product categorization
• Smart Search: Advanced filtering and sorting options
• Secure Payments: Multiple payment options with encrypted transactions
• Order Tracking: Real-time updates on order status
• Vendor Dashboard: Complete inventory and order management
• Analytics: Detailed reports and sales insights
• Mobile Responsive: Optimized for all device sizes

Admin Features:
• Product Management: Easy addition and updating of inventory
• Order Processing: Streamlined order fulfillment workflow
• User Management: Customer and vendor account administration
• Content Management: Dynamic content updates
• Promotions: Campaign and discount management
• Analytics Dashboard: Comprehensive business insights`,
    technologies: [FaJava, SiJavascript, SiApachetomcat, SiApachemaven],
    techNames: ["Java", "JavaScript", "Apache Tomcat", "Apache Maven"],
    techLinks: [
      "https://www.java.com/",
      "https://developer.mozilla.org/en-US/docs/Web/JavaScript",
      "https://tomcat.apache.org/",
      "https://maven.apache.org/",
    ],
    github: "",
    demo: "",
    image: "/projects/greenmart.png",
    images: [
      "/projects/greenmart.png",
      "/images/greenmart/Screenshot 2025-04-12 235635.png",
      "/images/greenmart/Screenshot 2025-04-12 235737.png",
      "/images/greenmart/Screenshot 2025-04-12 235829.png",
      "/images/greenmart/Screenshot 2025-04-12 235902.png",
      "/images/greenmart/Screenshot 2025-04-13 010900.png",
    ],
    available: true,
    backgroundColors: {
      base: [0.02, 0.12, 0.05] as [number, number, number], // Deep emerald green
      accent: [0.15, 0.35, 0.15] as [number, number, number], // Vibrant green
      intensity: 0.6, // More subtle intensity for better color balance
      mixStrength: 0.3, // Moderate mixing to preserve green tones
    },
  },
  {
    id: 6,
    name: "Music Factory",
    description:
      "A custom-developed portfolio website for a music artist to showcase their work, style, and creative journey.",
    pageDescription: `Music Factory is a custom-developed portfolio website designed for a music artist to professionally showcase their work, style, and creative journey. The site highlights albums, tracks, visuals, and upcoming projects in a clean and visually engaging layout.

Key Features:
• Music and album showcase section
• Artist biography and story presentation
• Gallery for visuals and cover arts
• Responsive layout for mobile, tablet, and desktop
• Custom UI with smooth navigation and visual emphasis

The goal was to create a smooth, modern, and artistic browsing experience that reflects the artist's identity and keeps fans engaged.`,
    technologies: [SiHtml5, SiCss3, SiJavascript],
    techNames: ["HTML5", "CSS3", "JavaScript"],
    techLinks: [
      "https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/HTML5",
      "https://developer.mozilla.org/en-US/docs/Web/CSS",
      "https://developer.mozilla.org/en-US/docs/Web/JavaScript",
    ],
    github: "",
    demo: "",
    image: "/images/musicfactroy/musicfactory mockup.png",
    images: [
      "/images/musicfactroy/musicfactory mockup.png",
      "/images/musicfactroy/Screenshot 2024-06-20 180146.jpeg",
      "/images/musicfactroy/Screenshot 2024-06-20 180256.jpeg",
      "/images/musicfactroy/Screenshot 2024-06-20 180326.jpeg",
      "/images/musicfactroy/Screenshot 2024-06-20 180440.jpeg",
    ],
    available: true,
    backgroundColors: {
      base: [0.0, 0.0, 0.0] as [number, number, number],
      accent: [0.9, 0.9, 0.9] as [number, number, number],
      intensity: 0.3,
      mixStrength: 0.2,
    },
  },
  {
    id: 7,
    name: "Sanoma",
    description:
      "A modern furniture e-commerce website with integrated payment gateway for seamless online shopping.",
    pageDescription: `Sanoma is a modern furniture e-commerce website built to enhance the brand's online presence and drive digital sales. The goal was to create a smooth, elegant, and user-friendly shopping experience where customers can easily browse products, view details, and complete secure orders.

Key Features:
• Full e-commerce functionality (Add to Cart, Checkout, Orders)
• Integrated Payment Gateway for secure online payments
• Product catalog with filtering and categorization
• Responsive UI designed for mobile and desktop
• Client-friendly CMS to manage products and content

The website was fully developed on WordPress with integrated payment processing to support online transactions.`,
    technologies: [SiWordpress, SiJavascript, SiHtml5, SiCss3],
    techNames: ["WordPress", "WooCommerce", "JavaScript", "HTML5", "CSS3"],
    techLinks: [
      "https://wordpress.org/",
      "https://woocommerce.com/",
      "https://developer.mozilla.org/en-US/docs/Web/JavaScript",
      "https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/HTML5",
      "https://developer.mozilla.org/en-US/docs/Web/CSS",
    ],
    github: "",
    demo: "",
    image: "/images/sanoma/sanoma mockup.png",
    images: [
      "/images/sanoma/sanoma mockup.png",
      "/images/sanoma/1.png",
      "/images/sanoma/2.png",
      "/images/sanoma/3.png",
    ],
    available: true,
    backgroundColors: {
      base: [0.96, 0.94, 0.9] as [number, number, number],
      accent: [0.4, 0.6, 0.85] as [number, number, number],
      intensity: 0.5,
      mixStrength: 0.3,
    },
  },
  {
    id: 8,
    name: "Storytime",
    description:
      "A custom movie ticket booking platform with interactive seat selection and seamless user experience.",
    pageDescription: `Storytime is a custom-built online movie ticket booking platform designed for a smooth and intuitive user experience. The system allows users to browse movies, view showtimes, and reserve seats with a clean, responsive, and easy-to-navigate interface.

Key Features:
• Interactive movie listings and schedule display
• Seat selection functionality
• Fully responsive UI for desktop and mobile
• Custom-built frontend with focus on UX

The goal was to create a streamlined booking journey without relying on external templates, delivering a clean and seamless booking experience.`,
    technologies: [SiHtml5, SiCss3, SiJavascript],
    techNames: ["HTML5", "CSS3", "JavaScript"],
    techLinks: [
      "https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/HTML5",
      "https://developer.mozilla.org/en-US/docs/Web/CSS",
      "https://developer.mozilla.org/en-US/docs/Web/JavaScript",
    ],
    github: "",
    demo: "",
    image: "/images/storytime/storytime mockup.png",
    images: [
      "/images/storytime/storytime mockup.png",
      "/images/storytime/1.jpeg",
      "/images/storytime/2.jpeg",
      "/images/storytime/3.jpeg",
      "/images/storytime/4.jpeg",
    ],
    available: true,
    backgroundColors: {
      base: [0.02, 0.0, 0.0] as [number, number, number],
      accent: [0.7, 0.05, 0.15] as [number, number, number],
      intensity: 0.6,
      mixStrength: 0.35,
    },
  },
  {
    id: 9,
    name: "DonutBet",
    description:
      "A betting platform build with multi-game support, a powerful admin panel, automated deposits, accurate logs, and fast withdrawal handling.",
    pageDescription: `DonutBet is a betting platform build focused on both player flow and operator control. The project was shaped to feel fast on the front end while also covering the practical admin tools needed to run daily platform activity cleanly.

Platform highlights:
- Multi-game coverage including Blackjack, Coin Flip, Towers, Mines, Crash, Chicken Road, CrateX, and Limbo
- Clean player flow built around quick navigation, strong visibility, and responsive interactions
- Structured wallet and transaction handling designed to keep balances, deposits, and withdrawals easy to track

Admin features:
- Powerful admin panel for managing users, balances, bets, and platform activity from one place
- Automated deposit handling to reduce manual work and keep crediting consistent
- Accurate logs for transactions, gameplay activity, and account-level actions
- Fast withdrawal management with clear status control and operator oversight
- Operational tooling built to support moderation, reviews, and day-to-day platform management

Build approach:
- Next.js frontend structure for scalable page composition and fast rendering
- JavaScript and Framer Motion used to keep the interface polished, responsive, and high-energy
- Backend-ready architecture prepared for wallet logic, admin workflows, and betting operations

The result is a platform experience that combines strong visual identity with serious back-office capability, making the product feel complete from both the user side and the admin side.`,
    technologies: [SiNextdotjs, SiJavascript, SiFramer, SiNodedotjs],
    techNames: ["Next.js", "JavaScript", "Framer Motion", "Node.js"],
    techLinks: [
      "https://nextjs.org/",
      "https://developer.mozilla.org/en-US/docs/Web/JavaScript",
      "https://www.framer.com/motion/",
      "https://nodejs.org/",
    ],
    github: "",
    demo: "https://donutbet.live/",
    image: "/images/donutbet/Screenshot 2026-03-17 215340.png",
    images: [
      "/images/donutbet/Screenshot 2026-03-17 215340.png",
      "/images/donutbet/Screenshot 2026-03-17 215559.png",
      "/images/donutbet/Screenshot 2026-03-17 215659.png",
      "/images/donutbet/Screenshot 2026-03-23 171428.png",
      "/images/donutbet/Screenshot 2026-03-23 171522.png",
      "/images/donutbet/Screenshot 2026-03-23 171730.png",
      "/images/donutbet/Screenshot 2026-03-23 171748.png",
      "/images/donutbet/Screenshot 2026-03-23 171803.png",
      "/images/donutbet/Screenshot 2026-03-23 171814.png",
      "/images/donutbet/Screenshot 2026-03-23 171834.png",
      "/images/donutbet/Screenshot 2026-03-23 171849.png",
      "/images/donutbet/Screenshot 2026-03-23 171911.png",
      "/images/donutbet/Screenshot 2026-03-23 171926.png",
      "/images/donutbet/Screenshot 2026-03-23 172031.png",
      "/images/donutbet/Screenshot 2026-03-23 172122.png",
      "/images/donutbet/Screenshot 2026-03-23 172133.png",
      "/images/donutbet/Screenshot 2026-03-23 172200.png",
      "/images/donutbet/Screenshot 2026-03-23 172211.png",
      "/images/donutbet/Screenshot 2026-03-23 172229.png",
      "/images/donutbet/Screenshot 2026-03-23 172253.png",
      "/images/donutbet/Screenshot 2026-03-23 172310.png",
    ],
    available: true,
    backgroundColors: {
      base: [0.0196, 0.0078, 0.0588] as [number, number, number],
      accent: [0.6784, 0.3686, 1.0] as [number, number, number],
      intensity: 0.5,
      mixStrength: 0.28,
    },
  },
  {
    id: 10,
    name: "Senuri Grand Castello",
    description:
      "An elegant hotel website built on WordPress, designed to showcase refined spaces, calm hospitality, and a memorable first impression.",
    pageDescription: `Senuri Grand Castello is a hotel website created to reflect the property's quiet elegance, spacious atmosphere, and welcoming character. The design direction focused on making the brand feel serene and premium from the very first screen, using clean composition, strong photography, and a softer green-and-white visual balance.

Project highlights:
- Large hospitality-focused hero presentation that immediately introduces the property with impact
- Refined section flow for showcasing the venue, surroundings, and guest experience in a calm, readable way
- Clean navigation and structured content that help visitors explore the hotel without friction
- Responsive layouts that preserve the sense of space and elegance across devices
- WordPress-based content management that makes ongoing updates practical for the client

Experience goals:
- Communicate warmth, comfort, and trust through simple but polished visual decisions
- Balance luxury styling with approachable usability so the site feels inviting rather than overwhelming
- Use imagery, spacing, and typography to support a more premium hospitality identity

Senuri Grand Castello offers a serene escape surrounded by quiet charm and warm hospitality. Designed to blend elegance with modern comfort, the website helps present the venue as a place to unwind, relax, and enjoy a refined stay. From spacious interiors to peaceful surroundings, every section works to make the property feel memorable before the visit even begins.`,
    technologies: [SiWordpress, SiJavascript, SiHtml5, SiCss3],
    techNames: ["WordPress", "JavaScript", "HTML5", "CSS3"],
    techLinks: [
      "https://wordpress.org/",
      "https://developer.mozilla.org/en-US/docs/Web/JavaScript",
      "https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/HTML5",
      "https://developer.mozilla.org/en-US/docs/Web/CSS",
    ],
    github: "",
    demo: "",
    image: "/images/senurigrouphotel/Screenshot 2026-03-17 210401.png",
    images: [
      "/images/senurigrouphotel/Screenshot 2026-03-17 210401.png",
      "/images/senurigrouphotel/Screenshot 2026-03-17 211118.png",
      "/images/senurigrouphotel/Screenshot 2026-03-17 211154.png",
      "/images/senurigrouphotel/Screenshot 2026-03-17 211235.png",
      "/images/senurigrouphotel/Screenshot 2026-03-17 211404.png",
      "/images/senurigrouphotel/Screenshot 2026-03-17 211432.png",
      "/images/senurigrouphotel/Screenshot 2026-03-17 211f334.png",
    ],
    available: true,
    backgroundColors: {
      base: [0.03, 0.09, 0.06] as [number, number, number],
      accent: [0.94, 0.97, 0.94] as [number, number, number],
      intensity: 0.35,
      mixStrength: 0.18,
    },
  },
];
